import { z } from "zod";

/**
 * Shared write-path coercions for step input sockets.
 *
 * A step's input schema is the boundary between whatever a source emits and the
 * Gently model, so normalizing a value's SHAPE belongs here rather than in every
 * graph that wires it. Compare `QUANTITY_INPUT`, which exists because
 * node-postgres returns NUMERIC as a string: same class of problem, same fix.
 */

/**
 * A date-only value (`YYYY-MM-DD`) arriving from an upstream source.
 *
 * Sources disagree about the separator: AS/400 GAINS DMDTRANS emits
 * `2026/07/08`, a CDC feed on a DATE column gives `2026-07-08`, and GAINS
 * SUPTRANS emits no separator at all (`20260708`). Without normalization the
 * other two spellings fail validation, and for a REQUIRED date that silently
 * drops the whole row — `upsertSalesOrdersFromRows` skips any line whose
 * `orderDate` does not parse. `dmdtransTransform.parseGainsDate` already did the
 * slash replacement in bespoke code; this is that rule moved to the boundary so
 * every source gets it.
 *
 * Why `preprocess` rather than the obvious alternatives (the reasoning mirrors
 * `QUANTITY_INPUT`):
 *
 * - `z.union([...])` of the accepted spellings would make the socket-type
 *   deriver fall through to a looser type; `preprocess` unwraps to its inner
 *   schema, so the socket stays correctly typed (`inferSocketTypeFromZod`
 *   unwraps `ZodPipe`/`ZodPreprocess` to the OUTPUT shape).
 * - `z.coerce.date()` would accept far more than a date-only string — including
 *   timestamps and numbers — and yields a `Date`, not the `YYYY-MM-DD` string
 *   these sockets bind to a DATE column.
 *
 * Only the SEPARATOR is normalized — a slash becomes a dash, and a compact
 * 8-digit run gains dashes. Both are unambiguous rewrites of a spelling that is
 * already year-month-day; nothing here reorders fields or guesses a century, so
 * a two-digit-year or day-first source still fails rather than being silently
 * mis-parsed. Blank and null pass through untouched so the inner schema (or a
 * `.nullable()` wrapper) decides, exactly as `QUANTITY_INPUT` leaves `""` alone
 * rather than turning it into a real value.
 *
 * A zero-year sentinel (`00000000`, `0000-00-00`) is NOT interpreted here, and
 * fails validation. That is correct for a REQUIRED date: the column is NOT NULL,
 * so there is nothing honest to substitute, and failing loudly beats writing a
 * guess. {@link OPTIONAL_DATE_ONLY_INPUT} does map it to null, because there the
 * absence is representable — see the note there.
 *
 * `0001-01-01` is deliberately NOT in that family. It is a real date, so a
 * source using it as a sentinel is making a claim only that source can
 * interpret; this socket takes it at face value.
 */
export const DATE_ONLY_INPUT = z.preprocess((value) => {
  if (typeof value !== "string" || value.trim() === "") return value;
  const trimmed = value.trim().replace(/\//g, "-");
  // `YYYYMMDD` — the fixed-width AS/400 spelling. Anchored on exactly 8 digits
  // so nothing else is reshaped: a 6- or 10-digit run is a different format
  // (or junk) and must fail loudly rather than be padded into a plausible date.
  return /^\d{8}$/.test(trimmed)
    ? `${trimmed.slice(0, 4)}-${trimmed.slice(4, 6)}-${trimmed.slice(6)}`
    : trimmed;
}, z.iso.date());

/**
 * A date-only value whose YEAR is `0000`, in either the compact or the
 * separated spelling (`00000000`, `0000-00-00`, `0000/00/00`).
 *
 * Matched on the year rather than on all-zeros so `00000101` is caught too —
 * same reach as `dmdtransTransform`'s `startsWith("0000")`, which is the
 * convention this repo already follows. No calendar has year 0000, so the value
 * carries no date information at all; it can only mean "not set".
 */
const ZERO_YEAR_DATE = /^0000(\d{4}|[-/]\d{2}[-/]\d{2})$/;

/**
 * {@link DATE_ONLY_INPUT} for a date a source may legitimately leave unset.
 *
 * Two spellings of "unset" become `null` here that the required base rejects: a
 * BLANK string and a zero-year sentinel. Both matter for the same reason —
 * socket input is validated BEFORE the handler runs, the failure is
 * non-retryable, and the batch path shares one activity per chunk, so one
 * unset date would take all 5,000 rows of its chunk with it.
 *
 * Blank is the important case, and it is the one a fixed-width source actually
 * produces: an empty column is spaces, and `""` is not `null`, so `.nullable()`
 * alone never caught it. Measured against 3,348,101 real SUPTRANS ingest rows,
 * `gtrcvdate` is SQL NULL on the 1,924,687 unreceived ones and the `00000000`
 * sentinel appears ZERO times — that particular spelling reaches this socket
 * only if a different writer stores the raw field instead of nulling it, which
 * is exactly what the staging table does (hence `suptransTransform`'s
 * `=== "00000000"` guard). Both are covered so the socket does not depend on
 * which writer is upstream.
 *
 * The asymmetry with the required base is the point: here "absent" has a
 * representation, so mapping either spelling onto it loses nothing. On a
 * required date the column is NOT NULL, so there is nothing honest to
 * substitute and the same values must still fail.
 *
 * `0001-01-01` is deliberately excluded from both: it is a real date, so a
 * source using it as a sentinel is making a claim only that source can
 * interpret.
 *
 * Deliberately still derived from the required base, so the SEPARATOR
 * normalization cannot drift between the two.
 *
 * `.optional()` is applied LAST, on the outside, and that ordering is
 * load-bearing rather than stylistic. Socket generation asks
 * `isFieldOptional`, which — unlike `inferSocketTypeFromZod` — inspects only
 * the TOP-LEVEL zod type and does not unwrap `ZodPipe`/`ZodPreprocess`. Wrapping
 * the optional in a preprocess therefore presents as `ZodPipe`, the socket is
 * generated `required: true`, and a step whose graph legitimately leaves the
 * date unwired can never satisfy its AND-join — it simply never runs. Keeping
 * `ZodOptional` outermost is what makes the socket read as optional.
 */
const OPTIONAL_DATE_ONLY_BASE = z.preprocess((value) => {
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  return trimmed === "" || ZERO_YEAR_DATE.test(trimmed) ? null : value;
}, DATE_ONLY_INPUT.nullable());

export const OPTIONAL_DATE_ONLY_INPUT = OPTIONAL_DATE_ONLY_BASE.optional();

/**
 * A whole number arriving from an upstream source.
 *
 * The integer sibling of `QUANTITY_INPUT`, and it exists for a broader reason
 * than that one: every column of an ingest foreign table is TEXT, so a socket
 * wired straight to one delivers `"3"`, not `3` — no NUMERIC round-trip needed.
 * A bare `z.number().int()` therefore fails with `expected number, received
 * string` on the FIRST row, and because input is validated before the handler
 * runs and the batch path shares one activity per chunk, that non-retryable
 * parse error takes down all 5,000 rows in the chunk.
 *
 * Same `preprocess` reasoning as `QUANTITY_INPUT`: a `number|string` union would
 * make `inferSocketTypeFromZod` fall through to `json` (so the editor would
 * accept any socket at all), and `z.coerce.number()` would turn `""` and `null`
 * into a real `0`. The guard leaves blank and null untouched so the inner schema
 * — or a `.nullable()` wrapper — decides.
 *
 * A non-integer like `"3.5"` still fails rather than being truncated to a
 * plausible-looking 3.
 */
export const INTEGER_INPUT = z.preprocess(
  (value) =>
    typeof value === "string" && value.trim() !== "" ? Number(value) : value,
  z.number().int(),
);
